import { useEffect, useState } from "react";
import { PartnerPicker } from "../components/PartnerPicker";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Sun, Loader2, Upload, Zap, TrendingUp, Leaf, PiggyBank, AlertTriangle, BatteryCharging } from "lucide-react";
import { toast } from "sonner";
import FreeDownloadGate from "../components/FreeDownloadGate";

const eur = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const num = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { maximumFractionDigits: 0 }));

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4 rounded border-border" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground mb-1"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`text-2xl font-bold ${accent ? "text-primary" : ""}`}>{value}</div>
    </Card>
  );
}

export default function FreeBolletta() {
  const [step, setStep] = useState(0);
  const [selfToken, setSelfToken] = useState(null);
  const [form, setForm] = useState({ address: "", citta: "", provincia: "", cap: "" });
  const [busy, setBusy] = useState(false);
  const [manualKwh, setManualKwh] = useState("");
  const [summary, setSummary] = useState(null);
  const [prod, setProd] = useState(null);
  const [probusy, setProbusy] = useState(false);
  const [countdown, setCountdown] = useState(120);
  useEffect(() => {
    if (!busy) return;
    setCountdown(120);
    const id = setInterval(() => setCountdown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [busy]);
  const countLabel = `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const carriedSelf = new URLSearchParams(window.location.search).get("self");
  const reteMode = new URLSearchParams(window.location.search).get("rete") === "1";
  const isChiaviInMano = new URLSearchParams(window.location.search).get("flow") === "chiaviinmano";
  const [client, setClient] = useState(null);
  const [copyClient, setCopyClient] = useState(false);
  const [partner, setPartner] = useState({ via_partner: false, partner_id: "", partner_name: "", partner_logo_url: "" });
  useEffect(() => {
    if (carriedSelf && reteMode) {
      api.get(`/public/self/${carriedSelf}/state`).then(({ data }) => { if (data && (data.address || data.city)) setClient(data); }).catch(() => {});
    }
  }, []);
  const toggleCopyClient = (checked) => {
    setCopyClient(checked);
    if (checked && client) setForm((f) => ({ ...f, citta: client.city || f.citta, provincia: client.provincia || f.provincia, address: client.address || f.address }));
  };

  const start = async () => {
    if (!form.citta.trim() && !form.address.trim()) return toast.error("Inserisci il comune (o la città più vicina)");
    setBusy(true);
    try {
      const { data } = await api.post("/public/free/start", { tool: "bolletta", ...form, ...partner, self_token: carriedSelf || undefined });
      setSelfToken(data.self_token); setStep(1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  const calcProd = async () => {
    if (!form.citta.trim() && !form.address.trim()) return toast.error("Inserisci il comune (o la città più vicina)");
    setProbusy(true);
    try {
      const { data } = await api.get("/public/pvgis", { params: { address: form.address, citta: form.citta, provincia: form.provincia } });
      setProd(data);
    } catch (e) { setProd(null); toast.error(e?.response?.data?.detail || "Località non trovata"); } finally { setProbusy(false); }
  };

  const runSummary = async () => {
    try {
      await api.post(`/public/self/${selfToken}/pvgis`, {});
      const { data } = await api.get(`/public/self/${selfToken}/summary`);
      setSummary(data); setStep(2);
    } catch (e) { toast.error("Non riusciamo a stimare la produzione per questa località"); } finally { setBusy(false); }
  };

  const uploadBill = async (file) => {
    if (!file) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/public/self/${selfToken}/bill`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bolletta analizzata"); await runSummary();
    } catch (e) { toast.error("Analisi non riuscita, usa l'inserimento manuale"); setBusy(false); }
  };

  const saveManual = async () => {
    const kwh = Number(manualKwh);
    if (!kwh || kwh <= 0) return toast.error("Inserisci il consumo annuo in kWh");
    setBusy(true);
    try { await api.put(`/public/self/${selfToken}/analysis`, { consumo_totale_kwh: kwh, periodo: "annuo" }); await runSummary(); }
    catch (e) { toast.error(e?.response?.data?.detail); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="free-bolletta-page">
      <header className="border-b border-border px-6 py-4 flex items-center gap-3">
        <Sun className="h-6 w-6 text-primary" />
        <div>
          <div className="text-lg font-bold leading-none">Analisi bolletta & risparmio</div>
          <div className="text-xs text-muted-foreground mt-0.5">Scopri gratis quanto puoi risparmiare con il fotovoltaico</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {step === 0 && (
          <Card className="p-6 rounded border-border" data-testid="free-step-address">
            <h2 className="text-base font-semibold mb-1">Dove si trova l'immobile?</h2>
            <p className="text-sm text-muted-foreground mb-4">Inserisci il <b>comune</b>: la produzione solare viene stimata sul comune, non sulla provincia.</p>
            {!reteMode && <div className="mb-5 pb-5 border-b border-border"><PartnerPicker value={partner} onChange={setPartner} /></div>}
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Comune *</Label><Input value={form.citta} onChange={(e) => set("citta", e.target.value)} data-testid="free-citta" placeholder="Verona" /></div>
              <div><Label className="text-xs">Provincia (opzionale)</Label><Input value={form.provincia} onChange={(e) => set("provincia", e.target.value)} data-testid="free-provincia" placeholder="VR" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Indirizzo (opzionale)</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="free-address" placeholder="Via Roma 1" /></div>
            </div>
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400" data-testid="free-comune-note">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>Se il tuo comune non viene riconosciuto, indica la <b>città più vicina</b>.</span>
            </div>
            {client && (
              <label className="mt-3 flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm cursor-pointer" data-testid="free-copy-client">
                <input type="checkbox" checked={copyClient} onChange={(e) => toggleCopyClient(e.target.checked)} data-testid="free-copy-client-checkbox" className="h-4 w-4" />
                <span>Usa i dati del cliente{client.name ? <> <b>{client.name}</b></> : null}: l'indirizzo dell'immobile coincide con quello del cantiere.</span>
              </label>
            )}
            <Button onClick={calcProd} disabled={probusy} variant={prod ? "secondary" : "default"} className="mt-4 gap-2" data-testid="free-calc-prod-btn">{probusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sun className="h-4 w-4" />} {prod ? "Ricalcola produzione" : "Calcola produzione"}</Button>

            {prod && (
              <div className="mt-6 border-t border-border pt-5" data-testid="free-pvgis-result">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Producibilità solare · {prod.location}</div>
                    <div className="text-3xl font-bold text-primary" data-testid="free-pvgis-annual">{num(prod.annual_kwh_per_kwp)} <span className="text-base font-medium text-muted-foreground">kWh/anno per kWp</span></div>
                    <div className="text-[11px] text-muted-foreground mt-1" data-testid="free-pvgis-source">Fonte dati: <b>PVGIS</b> · Commissione Europea (JRC)</div>
                  </div>
                </div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Produzione mese per mese (kWh per kWp)</div>
                <div className="space-y-1.5" data-testid="free-pvgis-monthly">
                  {(() => { const mx = Math.max(...prod.monthly.map((m) => m.kwh), 1); return prod.monthly.map((m) => (
                    <div key={m.mese} className="flex items-center gap-3 text-xs">
                      <span className="w-8 text-muted-foreground shrink-0">{m.mese}</span>
                      <div className="flex-1 h-2.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(m.kwh / mx) * 100}%` }} /></div>
                      <span className="w-16 text-right tabular-nums font-medium">{num(m.kwh)} kWh</span>
                    </div>
                  )); })()}
                </div>
                <Button onClick={start} disabled={busy} className="mt-5 w-full gap-2" data-testid="free-continue-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Continua →</Button>
              </div>
            )}
          </Card>
        )}

        {step === 1 && (
          <Card className="p-6 rounded border-border" data-testid="free-step-bill">
            <h2 className="text-base font-semibold mb-1">La tua bolletta</h2>
            <p className="text-sm text-muted-foreground mb-4">Carica una foto/PDF della bolletta per un'analisi automatica, oppure inserisci il consumo annuo.</p>
            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-border rounded p-6 cursor-pointer hover:border-primary transition-colors" data-testid="free-bill-upload">
              <Upload className="h-5 w-5 text-primary" />
              <span className="text-sm">{busy ? "Analisi in corso…" : "Carica bolletta (foto o PDF)"}</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" disabled={busy} onChange={(e) => uploadBill(e.target.files?.[0])} />
            </label>
            {busy && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2.5 text-sm text-red-600" data-testid="free-bolletta-loading" role="alert">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Analisi in corso: <b>potrebbero volerci massimo 120 secondi</b>. Non chiudere la pagina.
                  <span className="mt-1.5 flex items-center gap-1.5 font-bold text-base tabular-nums" data-testid="free-bolletta-countdown"><Loader2 className="h-4 w-4 animate-spin" /> {countLabel}</span>
                </span>
              </div>
            )}
            <div className="flex items-center gap-3 my-4"><div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">oppure</span><div className="h-px bg-border flex-1" /></div>
            <div className="flex items-end gap-3">
              <div className="flex-1"><Label className="text-xs">Consumo annuo (kWh)</Label><Input type="number" value={manualKwh} onChange={(e) => setManualKwh(e.target.value)} data-testid="free-manual-kwh" placeholder="es. 3500" /></div>
              <Button onClick={saveManual} disabled={busy} variant="secondary" data-testid="free-manual-btn">Calcola</Button>
            </div>
          </Card>
        )}

        {step === 2 && summary && (
          <div data-testid="free-step-result">
            <h2 className="text-xl font-bold mb-4">Ecco la tua stima gratuita</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              <Stat icon={Zap} label="Impianto" value={`${num(summary.pv_kwp)} kWp`} accent />
              {summary.storage_kwh != null && summary.storage_kwh > 0 && (
                <Stat icon={BatteryCharging} label="Accumulo" value={`${num(summary.storage_kwh)} kWh`} accent />
              )}
              <Stat icon={Sun} label="Produzione/anno" value={`${num(summary.produzione_annua_kwh)} kWh`} />
              <Stat icon={PiggyBank} label="Risparmio/anno" value={eur(summary.guadagno_anno)} accent />
              <Stat icon={TrendingUp} label="Risparmio 20 anni" value={eur(summary.guadagno_20anni)} />
              <Stat icon={Zap} label="Consumo" value={`${num(summary.consumo_annuo_kwh)} kWh`} />
              {(summary.prezzo_energia_kwh ?? summary.prezzo_usato) != null && (
                <Stat icon={PiggyBank} label={summary.prezzo_energia_kwh != null ? "Prezzo energia rilevato" : "Prezzo energia (stima)"} value={`${Number(summary.prezzo_energia_kwh ?? summary.prezzo_usato).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 3 })} €/kWh`} />
              )}
              <Stat icon={Sun} label="Autoconsumo" value={summary.autoconsumo_pct != null ? `${num(summary.autoconsumo_pct)}%` : "—"} />
              <Stat icon={Leaf} label="Copertura" value={summary.copertura_pct != null ? `${num(summary.copertura_pct)}%` : "—"} />
              <Stat icon={Leaf} label="CO₂ evitata/anno" value={summary.co2_annua_kg != null ? `${num(summary.co2_annua_kg)} kg` : "—"} />
            </div>
            <Card className="p-6 rounded border-primary/40 bg-primary/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <div className="font-semibold">{isChiaviInMano ? "Passo 2 di 2 · Misura il tetto" : "Ricevi la tua consulenza completa"}</div>
                <div className="text-sm text-muted-foreground">{isChiaviInMano ? "Ora disegna il tetto e genera il layout dei pannelli: la relazione Chiavi in mano unirà bolletta e tetto in un unico PDF." : "Analisi dettagliata di produzione, autoconsumo e risparmio — te la inviamo via email e la apri subito online."}</div>
              </div>
              {isChiaviInMano ? (
                <Button onClick={() => { window.location.href = `/free/tetto?self=${selfToken}&rete=1&flow=chiaviinmano`; }} className="gap-2 whitespace-nowrap" data-testid="free-continue-tetto-btn">Continua: misura il tetto →</Button>
              ) : (
                <FreeDownloadGate selfToken={selfToken} label="Scarica il PDF della consulenza" />
              )}
            </Card>
            <div className="mt-3 text-center">
              <Button variant="ghost" onClick={() => { setSummary(null); setManualKwh(""); setStep(1); }} className="gap-2 text-muted-foreground" data-testid="free-reupload-btn">
                <Upload className="h-4 w-4" /> Carica nuovamente la bolletta (in caso di errori)
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
